/**
 * Analytics TypeScript Interfaces and Types
 * Centralized type definitions for all tracking services
 */
import { isTrackingAllowed } from "@/lib/consent/privacy";

// ============================================================================
// Global Window Extensions
// ============================================================================

/**
 * Google Analytics gtag function type
 */
export type GtagFunction = (...args: unknown[]) => void;

/**
 * PostHog SDK interface
 */
export interface PostHogSDK {
  identify?: (
    distinctId: string,
    properties?: Record<string, unknown>,
    propertiesSetOnce?: Record<string, unknown>
  ) => void;
  capture?: (eventName: string, properties?: Record<string, unknown>) => void;
  reset?: () => void;
  opt_out_capturing?: (options?: { captureEventName?: string | null | false }) => void;
  opt_in_capturing?: (options?: { captureEventName?: string | null | false }) => void;
  has_opted_out_capturing?: () => boolean;
  has_opted_in_capturing?: () => boolean;
  register?: (properties: Record<string, unknown>) => void;
  setPersonProperties?: (properties: Record<string, unknown>) => void;
  isFeatureEnabled?: (key: string) => boolean;
  onFeatureFlags?: (callback: () => void) => void;
  getFeatureFlag?: (key: string) => boolean | string | undefined;
  reloadFeatureFlags?: () => void;
  get_distinct_id?: () => string;
  get_session_id?: () => string;
  sessionRecordingStarted?: () => boolean;
  startSessionRecording?: () => void;
  stopSessionRecording?: () => void;
  shutdown?: () => void;
}

/**
 * X Pixel (Twitter) tracking function type
 */
export type XPixelFunction = ((...args: unknown[]) => void) & {
  queue?: unknown[];
};

/**
 * Meta Pixel (Facebook) tracking function type
 */
export type MetaPixelFunction = ((...args: unknown[]) => void) & {
  queue?: unknown[];
  loaded?: boolean;
  version?: string;
  callMethod?: (...args: unknown[]) => void;
  push?: unknown;
};

/**
 * Lemon Squeezy window interface
 */
export interface LemonSqueezySDK {
  Setup: (config: { eventHandler: (event: LemonSqueezyEvent) => void }) => void;
  Url?: {
    Close: () => void;
    Open: (url: string) => void;
  };
  Refresh?: () => void;
}

/**
 * Extended Window interface with all tracking services
 */
declare global {
  interface Window {
    // Google Analytics
    dataLayer?: unknown[];
    gtag?: GtagFunction;

    // PostHog
    posthog?: PostHogSDK;
    __posthog?: {
      loaded?: boolean;
      [key: string]: unknown;
    };

    // X Pixel (Twitter)
    twq?: XPixelFunction;
    __xPixelInitialized?: boolean;
    __xPixelBlocked?: boolean;

    // Meta Pixel (Facebook)
    fbq?: MetaPixelFunction;
    _fbq?: MetaPixelFunction;
    __metaPixelInitialized?: boolean;
    __metaPixelBlocked?: boolean;

    // Lemon Squeezy
    LemonSqueezy?: LemonSqueezySDK;
    createLemonSqueezy?: () => void;

    // Consent Management
    blockAnalytics?: boolean;

    // Custom Tracking Functions
    trackLead?: (data: LeadData) => void;
    trackNewsletterSignup?: (data: NewsletterSignupData) => void;
    trackFormInteraction?: (stage: "start" | "submit", data: FormInteractionData) => void;
    xPixelTrack?: {
      trackLead: (data?: XPixelEventData) => void;
      trackDownload: (data?: XPixelEventData) => void;
      trackAddToCart: (data?: XPixelEventData) => void;
      trackPurchase: (data?: XPixelEventData) => void;
    };
  }
}

// ============================================================================
// Consent Management Types
// ============================================================================

/**
 * Consent state for analytics
 */
export type ConsentState = "granted" | "denied";

/**
 * Consent preferences stored in localStorage
 */
export interface ConsentPreferences {
  hasSetPreference: boolean;
  analytics: boolean;
  expires: number;
}

/**
 * Google Consent Mode configuration
 */
export interface GoogleConsentConfig {
  ad_storage: ConsentState;
  ad_user_data: ConsentState;
  ad_personalization: ConsentState;
  analytics_storage: ConsentState;
  functionality_storage?: ConsentState;
  personalization_storage?: ConsentState;
  security_storage?: ConsentState;
  wait_for_update?: number;
  region?: string[];
}

/**
 * Consent update event detail
 */
export interface ConsentUpdateEvent extends CustomEvent {
  detail: {
    state: ConsentState;
  };
}

// ============================================================================
// Event Tracking Types
// ============================================================================

/**
 * Ecommerce item data
 */
export interface EcommerceItem {
  item_id: string;
  item_name: string;
  item_category?: string;
  price: number;
  quantity: number;
}

/**
 * Ecommerce transaction data
 */
export interface EcommerceData {
  transaction_id?: string;
  value: number;
  currency: string;
  tax?: number;
  shipping?: number;
  items: EcommerceItem[];
}

/**
 * Generic event parameters
 */
export interface EventParameters {
  event_category?: string;
  event_label?: string;
  value?: number | string;
  currency?: string;
  send_to?: string;
  ecommerce?: EcommerceData;
  [key: string]: unknown;
}

/**
 * Lead generation data.
 *
 * `form_id` is the form's stable identity ("contact_form"), NOT where it was
 * found — placement goes in `form_source`. These were previously conflated,
 * which made "footer" and "install-slug" look like different forms.
 */
export interface LeadData {
  form_name: string;
  form_id: string;
  /** Where the form was encountered, e.g. "footer", "install_page". */
  form_source?: string;
  budget_range?: string;
  /** Lowest USD of `budget_range`, for GA4's numeric `value`. */
  budget_value?: number;
  currency?: string;
  /** GA4-native: how the lead arrived, e.g. "contact_page", "custom_dev_landing". */
  lead_source?: string;
  company_provided?: boolean;
  /** The submitter's email. Only ever sent to Google SHA-256 hashed (user_data), never as-is. */
  email?: string;
  [key: string]: unknown;
}

/**
 * Newsletter subscription data. Distinct from LeadData on purpose — this is
 * audience growth, not a sales lead.
 */
export interface NewsletterSignupData {
  form_id: string;
  form_source?: string;
  /** The subscriber's email. Only ever sent to Google SHA-256 hashed (user_data), never as-is. */
  email?: string;
  [key: string]: unknown;
}

/**
 * Form interaction data, used for form_start and form_submit.
 */
export interface FormInteractionData {
  form_id: string;
  form_name?: string;
  form_source?: string;
  [key: string]: unknown;
}

/**
 * X Pixel event data
 */
export interface XPixelEventData {
  value?: string;
  currency?: string;
  conversion_id?: string;
}

/**
 * Meta Pixel event data — matches Meta's documented standard event parameters.
 * See https://developers.facebook.com/docs/meta-pixel/reference#object-properties
 */
export interface MetaPixelEventData {
  value?: number;
  currency?: string;
  content_name?: string;
  content_ids?: string[];
  content_type?: "product" | "product_group";
  num_items?: number;
  /** Optional dedup key — used by the Conversion API in Phase 3 */
  event_id?: string;
}

/**
 * Lemon Squeezy checkout event
 */
/**
 * Fields of the LemonSqueezy Order object we read from a Checkout.Success.
 *
 * Everything is optional because the payload is untyped and LemonSqueezy
 * documents its shape two incompatible ways — see LemonSqueezyEvent.
 */
export interface LemonSqueezyOrderFields {
  /** Order UUID. The dedup key shared with the thank-you page. */
  identifier?: string;
  /** "The email address of the customer." (LS Order object) Sent to Google hashed only. */
  user_email?: string;
  /**
   * Per-store sequential number (1, 2, 3...). NOT the same value the
   * thank-you URL's [order_id] carries, so it is never a substitute for it.
   */
  order_number?: number | string;
  // LS Order objects carry `total` as a positive integer in CENTS of
  // the order currency (docs.lemonsqueezy.com/api/orders/the-order-object);
  // string kept in the union defensively since the payload is untyped.
  total?: number | string;
  currency?: string;
  /**
   * Not present on any documented Order payload — the product name is nested
   * in `first_order_item`. Kept in the union because reading both costs
   * nothing and the payload is untyped.
   */
  product_name?: string;
  first_order_item?: {
    product_name?: string;
    /** monthly / yearly / lifetime — the only place the plan is exposed. */
    variant_name?: string;
  };
}

/**
 * The Checkout.Success payload delivered by lemon.js.
 *
 * LemonSqueezy documents this TWO incompatible ways and does not say which is
 * current:
 *
 *   help/lemonjs/handling-events   -> { data: { type, id, attributes: {...} } }
 *   guides/developer-guide/lemonjs -> { data: { store_id, identifier, ... } }
 *
 * The first is the JSON:API Order object; the second is that object flattened.
 * The fields are therefore declared in BOTH positions and the reader coalesces
 * them, rather than betting on one and silently reading undefined.
 */
export interface LemonSqueezyEvent {
  event: "Checkout.Success" | string;
  data?: {
    /** Global order id, the value the thank-you URL's [order_id] carries. */
    id?: string | number;
    attributes?: LemonSqueezyOrderFields;
  } & LemonSqueezyOrderFields;
}

/**
 * Product view tracking data
 */
export interface ProductViewData {
  slug: string;
  name: string;
  alternateName?: string;
  price: string;
  category?: string;
}

/**
 * Purchase tracking data
 */
export interface PurchaseData {
  orderId?: string;
  orderIdentifier?: string;
  total?: string;
  /** The buyer's email. Only ever sent to Google SHA-256 hashed (user_data), never as-is. */
  email?: string;
  product: {
    slug: string;
    name: string;
    alternateName?: string;
    price: string;
  };
}

// ============================================================================
// Tracker Component Props
// ============================================================================

/**
 * PostHog Tracker Props
 */
export interface PostHogTrackerProps {
  apiKey: string;
  apiHost?: string;
}

// ============================================================================
// Type Guards
// ============================================================================

/**
 * Check if gtag is available
 */
export function isGtagAvailable(): boolean {
  return typeof window !== "undefined" && typeof window.gtag === "function";
}

/**
 * Check if PostHog is available
 */
export function isPostHogAvailable(): boolean {
  return typeof window !== "undefined" && typeof window.posthog === "object";
}

/**
 * Check if X Pixel is available
 */
export function isXPixelAvailable(): boolean {
  return (
    typeof window !== "undefined" && typeof window.twq === "function" && !window.__xPixelBlocked
  );
}

/**
 * Check if Meta Pixel is available
 */
export function isMetaPixelAvailable(): boolean {
  return (
    typeof window !== "undefined" && typeof window.fbq === "function" && !window.__metaPixelBlocked
  );
}

/**
 * Check if dataLayer is available
 */
export function isDataLayerAvailable(): boolean {
  return typeof window !== "undefined" && Array.isArray(window.dataLayer);
}

/**
 * Check if analytics consent is granted
 * Uses the resolved regional decision, stored choice, and GPC override.
 */
export function hasAnalyticsConsent(): boolean {
  return isTrackingAllowed();
}
